import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Grid3x3, AlertCircle, RefreshCcw, Search, X } from 'lucide-react';
import { getDomain } from '@/lib/utils';
import { CategorySkeleton } from '@/components/ui/skeleton';

interface Category {
  category: string;
  product_id: string;
  images: string[];
}

export function ModernCategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const domain = getDomain();
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/store/categories?domain=${domain}`);
      
      if (!response.ok) {
        throw new Error('Failed to connect to server');
      }

      const data = await response.json();
      if (!data.status) {
        throw new Error(data.message || 'Failed to load categories');
      }

      const uniqueCategories = data.products
        .filter((cat: Category) => cat.category && cat.category !== 'demo' && cat.category.trim() !== '')
        .reduce((acc: Category[], current: Category) => {
          const exists = acc.find(item => item.category === current.category);
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, []);

      setCategories(uniqueCategories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter(cat =>
    cat.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Categories - Store</title>
        <meta name="description" content="Browse products by category" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 mobile-page">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-brand-100 rounded-2xl">
                <Grid3x3 className="h-6 w-6 text-brand-600" />
              </div>
              <h1 className="text-3xl font-display font-bold text-gray-900">
                Categories
              </h1>
            </div>
            <p className="text-gray-600">
              Explore our {categories.length} product categories
            </p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-12"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Categories Grid */}
          {loading ? (
            <CategorySkeleton />
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={fetchCategories}
                className="inline-flex items-center gap-2 btn-primary"
              >
                <RefreshCcw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No categories found</h3>
              <p className="text-gray-600">Try a different search term</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredCategories.map((category, index) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="product-card"
                >
                  <Link
                    to={`/products?category=${encodeURIComponent(category.category)}`}
                    className="group block card overflow-hidden"
                  >
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={category.images[0]}
                        alt={category.category}
                        className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-0 flex items-end p-4">
                        <div className="w-full">
                          <h3 className="text-base font-semibold text-white mb-1">
                            {category.category}
                          </h3>
                          <div className="flex items-center gap-1 text-white/80 text-xs">
                            <span>View Products</span>
                            <svg className="h-3 w-3 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}


